import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@material-ui/core';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import FlightLandIcon from '@material-ui/icons/FlightLand';

/**
 * Get baggage category name in Hebrew
 */
const getBaggageCategoryName = (code, t) => {
  switch (code) {
    case 'T': return t('reservation.steps.baggageSelection.categories.trolley');
    case 'C': return t('reservation.steps.baggageSelection.categories.suitcase');
    case 'S': return t('reservation.steps.baggageSelection.categories.backpack');
    default: return '';
  }
};

/**
 * Get passenger group code from type
 */
const getPassengerGroupCode = (passengerType) => {
  switch (passengerType) {
    case 'adults': return 'A';
    case 'children': return 'C';
    case 'infants': return 'I';
    case 'seniors': return 'S';
    default: return 'A';
  }
};

/**
 * StepBaggageSelection - Step 3: Baggage selection
 * - Free baggage (price=0) is checked by default and disabled
 * - Auto-adds return baggage when outbound is selected
 */
const StepBaggageSelection = ({
  passengers = [],
  roomsArray = [],
  baggageOptions = [],
  baggageState = [],
  onBaggageChange,
  onContinue,
  currency = 'USD'
}) => {
  const { t } = useTranslation();
  const currencySymbol = currency === 'USD' ? '$' : currency;

  // Build flat list of passengers with their details (memoized to prevent unnecessary re-renders)
  const flatPassengers = useMemo(() => {
    const result = [];
    let globalIndex = 0;

    roomsArray.forEach((room) => {
      room.pax.forEach((pax) => {
        const formPassenger = passengers[globalIndex] || {};
        result.push({
          index: globalIndex,
          roomIndex: room.index,
          name: formPassenger.first_name && formPassenger.last_name
            ? `${formPassenger.first_name} ${formPassenger.last_name}`
            : t('reservation.steps.baggageSelection.passenger', { number: globalIndex + 1 }),
          type: pax.passenger,
          typeCode: getPassengerGroupCode(pax.passenger),
          typeHe: pax.passenger_he
        });
        globalIndex++;
      });
    });

    return result;
  }, [roomsArray, passengers]);

  // Separate baggage options by direction
  const outboundOptions = baggageOptions.find(b => b.direction === 'out');
  const inboundOptions = baggageOptions.find(b => b.direction === 'in');

  /**
   * Check if a baggage item is selected
   */
  const isBaggageSelected = useCallback((passengerIndex, direction, optionId) => {
    return baggageState.some(
      item =>
        item.passenger?.index === passengerIndex &&
        item.direction === direction &&
        item.baggage?.code === optionId
    );
  }, [baggageState]);

  /**
   * Handle baggage checkbox change with auto-add return logic
   */
  const handleBaggageChange = useCallback((
    isChecked,
    passenger,
    direction,
    option,
    directionIndex
  ) => {
    const baggageCategoryName = getBaggageCategoryName(option.category.code, t);

    const baggageData = {
      direction: direction,
      passenger: {
        index: passenger.index,
        type: passenger.typeCode,
      },
      baggage: {
        index: directionIndex,
        code: option.id,
        category: {
          id: option.category.code,
          name: baggageCategoryName
        },
        two_way: option.two_way,
        price: option.price.amount*1,
        weight: option.weight,
        dimensions: option.dimensions
      }
    };

    if (onBaggageChange) {
      // Handle this selection
      onBaggageChange(
        isChecked ? 'checked' : { target: { checked: false } },
        passenger.typeCode,
        baggageData,
        passenger.type,
        passenger.index,
        directionIndex,
        direction,
        option.price.amount*1
      );

      // Auto-add return baggage when selecting outbound (only for paid items)
      if (direction === 'out' && isChecked && option.price.amount*1 > 0) {
        // Find matching return option by category code
        const returnOption = inboundOptions?.options?.find(
          opt => opt.category.code === option.category.code &&
                 opt.passenger_groups_codes.includes(passenger.typeCode)
        );

        if (returnOption && !isBaggageSelected(passenger.index, 'in', returnOption.id)) {
          const returnIndex = baggageOptions.findIndex(b => b.direction === 'in');
          const returnBaggageData = {
            direction: 'in',
            passenger: {
              index: passenger.index,
              type: passenger.typeCode,
            },
            baggage: {
              index: returnIndex,
              code: returnOption.id,
              category: {
                id: returnOption.category.code,
                name: baggageCategoryName
              },
              two_way: returnOption.two_way,
              price: returnOption.price.amount*1,
              weight: returnOption.weight,
              dimensions: returnOption.dimensions
            }
          };

          // Small delay to avoid state conflicts
          setTimeout(() => {
            onBaggageChange(
              'checked',
              passenger.typeCode,
              returnBaggageData,
              passenger.type,
              passenger.index,
              returnIndex,
              'in',
              returnOption.price.amount*1
            );
          }, 100);
        }
      }
    }
  }, [onBaggageChange, inboundOptions, baggageOptions, isBaggageSelected]);

  /**
   * Render baggage options for a specific direction and passenger
   */
  const renderBaggageOptions = (passenger, direction, options, directionIndex) => {
    if (!options?.options) return null;

    return options.options
      .filter(option => option.passenger_groups_codes.includes(passenger.typeCode))
      .map((option, idx) => {
        const baggageCategoryName = getBaggageCategoryName(option.category.code, t);
        const isSelected = isBaggageSelected(passenger.index, direction, option.id);
        const isFree = option.price.amount*1 === 0;

        // Auto-add free baggage to state if not already selected
        // This runs once per free item and doesn't cause loops because of the isSelected check
        if (isFree && !isSelected && onBaggageChange) {
          handleBaggageChange(true, passenger, direction, option, directionIndex);
        }

        const dimensionsText = option.dimensions
          ? `${t('reservation.steps.baggageSelection.dimensions')} ${option.dimensions.height}×${option.dimensions.width}×${option.dimensions.length} ${option.dimensions.unit === 'cm' ? t('common.units.cm_full') : option.dimensions.unit} `
          : '';
        const optionLabel = `${baggageCategoryName} ${dimensionsText}${option.weight.amount} ${option.weight.unit === 'kg' ? t('common.units.kg_full') : option.weight.unit} - ${isFree ? t('common.labels.free') : `${option.price.amount} ${currency === 'USD' ? t('reservation.steps.baggageSelection.currency.usd') : currency}`}`;

        return (
          <div
            key={`${direction}-${option.id}-${idx}`}
            className={`baggage-option ${isSelected ? 'selected' : ''} ${isFree ? 'free' : ''}`}
            role="option"
            aria-selected={isSelected || isFree}
            aria-label={optionLabel}
            tabIndex={isFree ? -1 : 0}
            onClick={() => {
              if (!isFree) {
                handleBaggageChange(!isSelected, passenger, direction, option, directionIndex);
              }
            }}
            onKeyPress={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !isFree) {
                handleBaggageChange(!isSelected, passenger, direction, option, directionIndex);
              }
            }}
          >
            <div className="baggage-label-headers">
                <div className="baggage-label">{baggageCategoryName}</div>
                <div className="baggage-label baggage-label--weight">
                {option.weight.amount} {option.weight.unit === 'kg' ? t('common.units.kg') : option.weight.unit}
                </div>
            </div>
            <div className="baggage-icon-and-dimensions">
                <div
                className={`baggage-icon ${option.category.code === 'S' ? 'backpack' : option.category.code === 'T' ? 'trolley' : 'suitcase'}`}
                aria-hidden="true"
                title={baggageCategoryName}
                />
                {option.dimensions && (
                <div className="baggage-label baggage-label--dimensions">
                    {option.dimensions.height}×{option.dimensions.width}×{option.dimensions.length} {option.dimensions.unit === 'cm' ? t('common.units.cm') : option.dimensions.unit}
                </div>
                )}
            </div>
            <div className="baggage-price-and-checkbox">

                <Checkbox
                    checked={isSelected || isFree}
                    disabled={isFree}
                    size="small"
                    className="baggage-checkbox"
                    inputProps={{ 'aria-label': optionLabel }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        if (!isFree) {
                            handleBaggageChange(e.target.checked, passenger, direction, option, directionIndex);
                        }
                    }}
                />
                <div className={`baggage-price ${isFree ? 'free' : ''}`}>
                {isFree ? t('common.labels.free') : `${currencySymbol}${option.price.amount}`}
                </div>
            </div>
          </div>
        );
      });
  };

  // Calculate totals
  const paidBaggageCount = baggageState.filter(b => b.baggage?.price > 0).length;
  const totalBaggagePrice = baggageState.reduce((sum, item) => sum + (item.baggage?.price || 0), 0);

  return (
    <div className="step-baggage-selection" role="region" aria-label={t('reservation.steps.baggageSelection.title')}>
      {/* Info bar */}
      {/*<div className="wizard-info-bar" role="note">
        על מנת לחסוך בעלויות הכבודה, מומלץ לרכוש מזוודות יד (טרולי) שתוכלו להעלות איתכם למטוס
      </div>*/}

      {/* Auto-return notice */}
      <div className="wizard-info-bar wizard-info-bar--highlight" role="alert">
        <strong>{t('package.modals.passengersChange.heading')}:</strong> {t('reservation.steps.baggageSelection.autoReturnNotice')}
      </div>

      {/* Flat list of passengers */}
      {flatPassengers.map((passenger) => (
        <div
          key={`passenger-${passenger.index}`}
          className="wizard-passenger-card"
          role="group"
          aria-label={`${t('reservation.steps.baggageSelection.title')} ${passenger.name}`}
        >
          {/* Passenger header - same style as Step 2, but with actual name */}
          <div className="wizard-passenger-header" id={`passenger-baggage-header-${passenger.index}`}>
            {t('reservation.steps.baggageSelection.passenger', { number: passenger.index + 1 })}: {passenger.typeHe}
            {passenger.name !== t('reservation.steps.baggageSelection.passenger', { number: passenger.index + 1 }) && (
              <span className="passenger-name-display"> - {passenger.name}</span>
            )}
          </div>

          {/* Outbound baggage */}
          {outboundOptions && (
            <div className="baggage-direction-section" role="group" aria-label={t('reservation.steps.baggageSelection.outbound')}>
              <div className="baggage-direction-label">
                <FlightTakeoffIcon className="baggage-direction-icon" aria-hidden="true" />
                {t('reservation.steps.baggageSelection.outbound')}
              </div>
              <div className="baggage-options" role="listbox" aria-label={t('reservation.steps.baggageSelection.outbound')}>
                {renderBaggageOptions(
                  passenger,
                  'out',
                  outboundOptions,
                  baggageOptions.findIndex(b => b.direction === 'out')
                )}
              </div>
            </div>
          )}

          {/* Inbound baggage */}
          {inboundOptions && (
            <div className="baggage-direction-section" role="group" aria-label={t('reservation.steps.baggageSelection.inbound')}>
              <div className="baggage-direction-label">
                <FlightLandIcon className="baggage-direction-icon" aria-hidden="true" />
                {t('reservation.steps.baggageSelection.inbound')}
              </div>
              <div className="baggage-options" role="listbox" aria-label={t('reservation.steps.baggageSelection.inbound')}>
                {renderBaggageOptions(
                  passenger,
                  'in',
                  inboundOptions,
                  baggageOptions.findIndex(b => b.direction === 'in')
                )}
              </div>
            </div>
          )}
        </div>
      ))}


      {/* Continue Button */}
      <button
        type="button"
        className="step-continue-btn"
        onClick={onContinue}
        aria-label={t('common.buttons.continue')}
      >
        {t('common.buttons.continue')}
      </button>
    </div>
  );
};

export default StepBaggageSelection;
